import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, KeyRound, Plus, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Field, FormError } from '@/components/form';
import { EmptyState, ErrorState, PageHeader, Section } from '@/components/page';
import { ScopeCheckboxes } from '@/features/clients/scope-checkboxes';
import { scopesQuery } from '@/features/clients/scopes';
import { StatusPill } from '@/features/register/badges';
import { actAs } from '@/lib/act-as';
import { api, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/auth';
import { formatDate } from '@/lib/labels';
import type { ClientDetail, ClientKey } from '@/lib/types';

export const Route = createFileRoute('/_app/clients/$clientId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me?.canManageClients) throw redirect({ to: '/entries', search: {} });
  },
  component: ClientPage,
});

const ALGORITHMS = ['ES256', 'ES384', 'RS256', 'PS256', 'EdDSA'];

function ClientPage() {
  const { clientId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const client = useQuery({ queryKey: ['admin', 'client', clientId], queryFn: () => api.get<ClientDetail>(`/v1/admin/clients/${clientId}`) });
  const scopes = useQuery(scopesQuery);
  const [name, setName] = useState('');
  const [allowed, setAllowed] = useState<string[]>([]);
  const [readsAll, setReadsAll] = useState(false);
  const [addingKey, setAddingKey] = useState(false);
  const [revoking, setRevoking] = useState<ClientKey | null>(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (client.data) {
      setName(client.data.name);
      setAllowed(client.data.allowedScopes);
      setReadsAll(client.data.readsAllClients);
    }
  }, [client.data]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'client', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
  };
  const save = useMutation({
    mutationFn: (patch: Partial<Pick<ClientDetail, 'name' | 'allowedScopes' | 'readsAllClients' | 'active'>>) => api.patch(`/v1/admin/clients/${clientId}`, patch),
    onSuccess: () => {
      toast.success('Client updated');
      setToggling(false);
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (key: ClientKey) => api.post(`/v1/admin/clients/${clientId}/keys/${key.id}/revoke`),
    onSuccess: () => {
      toast.success('Key revoked; tokens signed with it stop working within a minute');
      setRevoking(null);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (client.isError) return <ErrorState error={client.error} onRetry={() => void client.refetch()} />;
  if (!client.data) return <Skeleton className="h-96 w-full" />;
  const c = client.data;
  const dirty = name.trim() !== c.name || readsAll !== c.readsAllClients || [...allowed].sort().join() !== [...c.allowedScopes].sort().join();

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ name: name.trim(), allowedScopes: allowed, readsAllClients: readsAll });
  }

  return (
    <>
      <PageHeader
        back={
          <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Clients
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {c.name} {c.active ? <StatusPill tone="active">Active</StatusPill> : <StatusPill tone="removed">Disabled</StatusPill>}
          </span>
        }
        description={<>Slug <code className="font-mono">{c.slug}</code> · created {formatDate(c.createdAt)} · {c.userCount} user(s)</>}
        actions={
          <>
            <Button
              variant="outline"
              onClick={async () => {
                actAs.set(c.id);
                await queryClient.invalidateQueries();
                await navigate({ to: '/users', search: {} });
              }}
            >
              <Users /> Manage users
            </Button>
            <Button variant={c.active ? 'outline' : 'default'} onClick={() => setToggling(true)}>
              {c.active ? 'Disable client' : 'Re-enable client'}
            </Button>
          </>
        }
      />

      <div className="grid gap-5">
        <Section title="Settings">
          <form onSubmit={submit} className="grid gap-5 p-5">
            <FormError message={save.isError ? errorMessage(save.error) : null} />
            <Field id="client-name" label="Name" className="max-w-md">
              <Input id="client-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            </Field>
            {scopes.data ? <ScopeCheckboxes all={scopes.data} value={allowed} onChange={setAllowed} /> : null}
            <div className="flex items-start gap-2.5">
              <Checkbox id="reads-all" checked={readsAll} onCheckedChange={(v) => setReadsAll(v === true)} className="mt-0.5" />
              <Label htmlFor="reads-all" className="grid gap-0.5 font-normal">
                <span className="text-sm font-medium">Can see every client’s entries</span>
                <span className="text-xs text-muted-foreground">Applies to its API tokens and its users. It can still only change its own.</span>
              </Label>
            </div>
            <div>
              <Button type="submit" disabled={!dirty || !name.trim() || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Section>

        <Section
          title="API keys"
          description="Public keys this client’s systems sign their tokens with. The private key never leaves the client."
          actions={
            <Button size="sm" variant="outline" onClick={() => setAddingKey(true)}>
              <Plus /> Add key
            </Button>
          }
        >
          {c.keys.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No keys yet">
                Ask the client’s developers for a public key (SPKI PEM). See <code className="font-mono">docs/integration.md</code> for how they generate one.
              </EmptyState>
            </div>
          ) : (
            <ul className="divide-y">
              {c.keys.map((k) => (
                <li key={k.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <KeyRound className="size-5 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      <code className="font-mono">{k.kid}</code> <span className="text-muted-foreground">· {k.algorithm}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Added {formatDate(k.createdAt)}
                      {k.revokedAt ? ` · revoked ${formatDate(k.revokedAt)}` : ''}
                    </div>
                  </div>
                  {k.revokedAt ? (
                    <StatusPill tone="removed">Revoked</StatusPill>
                  ) : (
                    <>
                      <StatusPill tone="active">Active</StatusPill>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRevoking(k)}>
                        Revoke
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <AddKeyDialog clientId={c.id} open={addingKey} onOpenChange={setAddingKey} onAdded={refresh} />

      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke key {revoking?.kid}?</AlertDialogTitle>
            <AlertDialogDescription>Any system still signing with it will be refused. Add its replacement first if you’re rotating keys. This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => revoking && revoke.mutate(revoking)}>
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={toggling} onOpenChange={setToggling}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{c.active ? `Disable ${c.name}?` : `Re-enable ${c.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {c.active
                ? 'Its API tokens are refused and its users can’t sign in. Its register data stays in place and still counts in lookups by clients that can see all entries.'
                : 'Its API keys and users will work again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => save.mutate({ active: !c.active })}>{c.active ? 'Disable client' : 'Re-enable client'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AddKeyDialog({ clientId, open, onOpenChange, onAdded }: { clientId: string; open: boolean; onOpenChange: (o: boolean) => void; onAdded: () => void }) {
  const [kid, setKid] = useState('');
  const [algorithm, setAlgorithm] = useState('ES256');
  const [pem, setPem] = useState('');
  const add = useMutation({
    mutationFn: () => api.post(`/v1/admin/clients/${clientId}/keys`, { kid: kid.trim(), algorithm, publicKeyPem: pem.trim() }),
    onSuccess: () => {
      toast.success(`Key ${kid} added`);
      onOpenChange(false);
      setKid('');
      setPem('');
      onAdded();
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>Add a public key</DialogTitle>
            <DialogDescription>Paste the public key only. Never accept or paste a private key.</DialogDescription>
          </DialogHeader>
          <FormError message={add.isError ? errorMessage(add.error) : null} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="key-kid" label="Key id (kid)" hint="Must match the kid header in their tokens, e.g. 2026-09.">
              <Input id="key-kid" value={kid} onChange={(e) => setKid(e.target.value)} className="font-mono" maxLength={64} />
            </Field>
            <div className="grid gap-1.5">
              <Label id="key-alg-label">Algorithm</Label>
              <Select value={algorithm} onValueChange={setAlgorithm}>
                <SelectTrigger aria-labelledby="key-alg-label" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALGORITHMS.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Field id="key-pem" label="Public key (PEM)">
            <Textarea id="key-pem" value={pem} onChange={(e) => setPem(e.target.value)} rows={7} className="font-mono text-xs" placeholder={'-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----'} spellCheck={false} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!kid.trim() || !pem.includes('BEGIN') || add.isPending}>
              {add.isPending ? 'Adding…' : 'Add key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
